import type ConstructionPartsDto from "../../dto/construction/ConstructionPartsDto";
import type {PartDto} from "./types/PartDto";
import type {PartItemDto} from "./types/PartItemDto";
import type {ConstructionStateNew} from "./constructionNewSlice";
import type {SelectedEntity} from "./types/SelectedEntity";
import type MastPartsDto from "../../dto/construction/parts/MastPartsDto";
import type BeamPartsDto from "../../dto/construction/parts/BeamPartsDto";
import type SupportArmPartsDto from "../../dto/construction/parts/SupportArmPartsDto";
import type CatenaryPartsDto from "../../dto/construction/parts/CatenaryPartsDto";
import type HingedCantileverPartsDto from "../../dto/construction/parts/HingedCantileverPartsDto";
import type DevicePartsDto from "../../dto/construction/parts/DevicePartsDto";
import type WirePartsDto from "../../dto/construction/parts/WirePartsDto";
import type CrossSpanWirePartsDto from "../../dto/construction/parts/CrossSpanWirePartsDto";
import type RegistrationArmDto from "../../dto/construction/parts/RegistrationArmDto";
import type SteadyArmDto from "../../dto/construction/parts/SteadyArmDto";
import type CrossBeamDto from "../../dto/construction/parts/CrossBeamDto";
import type GuiValueDto from "../../dto/construction/GuiValueDto";

type Part =
| (MastPartsDto & { type: "masts" })
| (BeamPartsDto & { type: "beams" })
| (SupportArmPartsDto & { type: "supportArms" })
| (CrossSpanWirePartsDto & { type: "crossSpanWires"})
| (CatenaryPartsDto & { type: "catenaries" })
| { crossBeams: CrossBeamDto[]; type: "crossBeams"; }
| { steadyArms: SteadyArmDto[]; type: "steadyArms"; }
| { registrationArms: RegistrationArmDto[]; type: "registrationArms"; }
| (HingedCantileverPartsDto & { type: "hingedCantilevers" })
| (DevicePartsDto & { type: "devices" })
| (WirePartsDto & { type: "wires" })

function mapPartDtoToPart(part: PartDto): Part[] {
  const parts: Part[] = [];

  if ("masts" in part) {
    parts.push({ ...part, type: "masts" });
  }

  if ("beams" in part) {
    parts.push({ ...part, type: "beams" });
  }

  if ("supportArms" in part) {
    parts.push({ ...part, type: "supportArms" });
  }

  if ("crossSpanWires" in part) {
    parts.push({ ...part, type: "crossSpanWires" });
  }

  if ("catenaries" in part) {
    parts.push({ ...part, type: "catenaries" });
  }  

  if ("hingedCantilevers" in part) {
    parts.push({ ...part, type: "hingedCantilevers" });
  }

  if ("devices" in part) {
    parts.push({ ...part, type: "devices" });
  }

  if ("wires" in part) {
      parts.push({ ...part, type: "wires" });
  }

  if ("crossBeams" in part) {
    parts.push({ ...part, type: "crossBeams"})
  }

  if ("steadyArms" in part) {
    parts.push({ ...part, type: "steadyArms"})
  }

  if ("registrationArms" in part) {
    parts.push({ ...part, type: "registrationArms"})
  }

  return parts;
}

function hasCompartment(item: PartItemDto): item is PartItemDto & { compartment: GuiValueDto<number> } {
  return "compartment" in item && item.compartment && typeof item.compartment === "object" && "value" in item.compartment && typeof item.compartment.value === "number";
}

function hasPosition(item: PartItemDto): item is PartItemDto & { position: GuiValueDto<number> } {
  return "position" in item && item.position && typeof item.position === "object" && "value" in item.position && typeof item.position.value === "number";
}

function getAllConstructionElements(
  constructionParts: ConstructionPartsDto | undefined
): SelectedEntity[] {
  if (!constructionParts) return [];

  const constructionElements: SelectedEntity[] = [];
  const parts: PartDto[] = Object.values(constructionParts).flat();

  const mappedParts: Part[] = parts.flatMap(mapPartDtoToPart);
  for (const part of mappedParts) {
    const entityType = part.type;
    let partItems: PartItemDto[] = [];

    switch (part.type) {
      case "masts":
        partItems = part.masts;
        break;
      case "beams":
        partItems = part.beams;
        break;
      case "supportArms":
        partItems = part.supportArms;
        break;
      case "catenaries":
        partItems = part.catenaries;
        break;
      case "registrationArms":
        partItems = part.registrationArms;
        break;
      case "steadyArms":
        partItems = part.steadyArms;
        break;
      case "crossBeams":
        partItems = part.crossBeams;
        break;
      case "hingedCantilevers":
        partItems = part.hingedCantilevers;
        break;
      case "devices":
        partItems = part.devices;
        break;
      case "wires":
        partItems = part.wires;
        break;
      case "crossSpanWires":
        partItems = part.crossSpanWires;
    }

    constructionElements.push(
      ...partItems.map(item => ({
        entityId: item.id,
        isSelected: item.isSelected,
        parentId: item.parentId,
        compartment: hasCompartment(item) ? item.compartment.value : undefined,
        position: hasPosition(item) ? item.position.value : undefined,
        entityType, 
      }))
    );
  }

  return constructionElements;
}

const getChildConstructionElements = (
  id: string,
  constructionElementsToUpdate: string[],
  state: ConstructionStateNew,
): void => {
  const childElements = state.constructionElements.filter(ce => ce.parentId === id);
  childElements.forEach(ce => {
    if (ce.entityId) {
      if (!constructionElementsToUpdate.includes(ce.entityId)) {
        constructionElementsToUpdate.push(ce.entityId);
        getChildConstructionElements(ce.entityId, constructionElementsToUpdate, state);
      }
    }
  });
};

export {
  getAllConstructionElements,
  getChildConstructionElements,
};
